import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ThemeToggle } from '../components/ThemeToggle';
import { ArrowLeft, Plus, Minus, Undo2, Trash2, Wallet } from 'lucide-react';
import axios from 'axios';
import { useConfirm } from '../contexts/ConfirmContext';
import { toast } from 'sonner';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  active: { label: 'AKTYWNY', className: 'text-green-500 bg-green-500/10 border-green-500/30' },
  completed: { label: 'ZAKOŃCZONY', className: 'text-[#A1A1A1] bg-[#2A2A2A]/40 border-[#2A2A2A]' },
  cancelled: { label: 'ANULOWANY', className: 'text-red-400 bg-red-500/10 border-red-500/30' },
};

export const PackagesPage = () => {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [packageTypes, setPackageTypes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({
    package_type: '',
    price: '',
    purchased_date: localISODate(),
    payment_due_date: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientRes, typesRes, packagesRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/packages/types`, { withCredentials: true }),
        axios.get(`${API_URL}/api/packages/client/${id}`, { withCredentials: true }),
      ]);
      setClient(clientRes.data);
      setPackageTypes(typesRes.data);
      setPackages(packagesRes.data);
      if (typesRes.data.length > 0 && !form.package_type) {
        setForm(prev => ({ ...prev, package_type: typesRes.data[0].key }));
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.package_type || !form.price) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/packages`, {
        client_id: id,
        package_type: form.package_type,
        price: parseFloat(form.price),
        purchased_date: form.purchased_date,
        payment_due_date: form.payment_due_date || null,
        notes: form.notes || null,
      }, { withCredentials: true });
      setPackages([res.data, ...packages]);
      setDialogOpen(false);
      setForm({ package_type: packageTypes[0]?.key || '', price: '', purchased_date: localISODate(), payment_due_date: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd zapisu pakietu');
    } finally {
      setSaving(false);
    }
  };

  const handleUseSession = async (pkg) => {
    setBusyId(pkg.id);
    try {
      const res = await axios.post(`${API_URL}/api/packages/${pkg.id}/use-session`, {}, { withCredentials: true });
      setPackages(packages.map(p => p.id === pkg.id ? res.data : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd');
    } finally {
      setBusyId(null);
    }
  };

  const handleUndoSession = async (pkg) => {
    setBusyId(pkg.id);
    try {
      const res = await axios.post(`${API_URL}/api/packages/${pkg.id}/undo-session`, {}, { withCredentials: true });
      setPackages(packages.map(p => p.id === pkg.id ? res.data : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd');
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (pkg) => {
    if (!await confirm(`Anulować pakiet "${pkg.package_label}"?`)) return;
    setBusyId(pkg.id);
    try {
      const res = await axios.put(`${API_URL}/api/packages/${pkg.id}`, { status: 'cancelled' }, { withCredentials: true });
      setPackages(packages.map(p => p.id === pkg.id ? res.data : p));
    } catch (error) {
      toast.error('Błąd anulowania pakietu');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (pkg) => {
    if (!await confirm(`Usunąć pakiet "${pkg.package_label}" na stałe?`)) return;
    setBusyId(pkg.id);
    try {
      await axios.delete(`${API_URL}/api/packages/${pkg.id}`, { withCredentials: true });
      setPackages(packages.filter(p => p.id !== pkg.id));
    } catch (error) {
      toast.error('Błąd usuwania pakietu');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  const activePackages = packages.filter(p => p.status === 'active');
  const totalSpent = packages.filter(p => p.status !== 'cancelled').reduce((sum, p) => sum + p.price, 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="packages-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Button onClick={() => navigate(`/client/${id}`)} variant="ghost" className="text-[#A1A1A1] hover:text-white px-2 sm:px-4" data-testid="back-to-client">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Powrót</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <Wallet className="w-7 h-7 text-[#C9A84C]" />
            <h1 className="display-text text-4xl text-white">Pakiety</h1>
          </div>
          <p className="body-text text-[#A1A1A1] text-lg">{client?.name}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
          <div className="card-surface rounded-3xl p-6">
            <div className="label-text text-[#A1A1A1] text-xs mb-2">AKTYWNE PAKIETY</div>
            <div className="text-3xl font-bold text-white">{activePackages.length}</div>
          </div>
          <div className="card-surface rounded-3xl p-6">
            <div className="label-text text-[#A1A1A1] text-xs mb-2">WSZYSTKICH PAKIETÓW</div>
            <div className="text-3xl font-bold text-white">{packages.length}</div>
          </div>
          <div className="card-surface rounded-3xl p-6 col-span-2 md:col-span-1">
            <div className="label-text text-[#A1A1A1] text-xs mb-2">SUMA (BEZ ANULOWANYCH)</div>
            <div className="text-3xl font-bold text-[#C9A84C]">{totalSpent.toFixed(2)} zł</div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <h2 className="heading-text text-2xl text-white">Historia pakietów</h2>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full"
                data-testid="new-package-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nowy pakiet
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-md">
              <DialogHeader>
                <DialogTitle className="heading-text text-2xl text-white">Nowy pakiet</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-5 mt-2">
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">TYP PAKIETU</Label>
                  <Select value={form.package_type} onValueChange={(v) => setForm({ ...form, package_type: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white" data-testid="package-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                      {packageTypes.map(t => (
                        <SelectItem key={t.key} value={t.key}>
                          {t.label}{t.sessions ? ` (${t.sessions} sesji)` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">CENA (ZŁ)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="np. 800"
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                    required
                    data-testid="package-price-input"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">DATA ZAKUPU</Label>
                  <Input
                    type="date"
                    value={form.purchased_date}
                    onChange={(e) => setForm({ ...form, purchased_date: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">TERMIN NASTĘPNEJ PŁATNOŚCI (OPCJONALNIE)</Label>
                  <Input
                    type="date"
                    value={form.payment_due_date}
                    onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                    data-testid="package-payment-due-input"
                  />
                </div>
                <div>
                  <Label className="label-text text-[#A1A1A1] mb-2 block">NOTATKA (OPCJONALNIE)</Label>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full h-12"
                  data-testid="save-package-button"
                >
                  {saving ? 'Zapisywanie...' : 'Zapisz pakiet'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {packages.length === 0 ? (
          <div className="card-surface rounded-3xl p-12 text-center">
            <Wallet className="w-12 h-12 text-[#A1A1A1] mx-auto mb-4" />
            <p className="body-text text-[#A1A1A1]">Klient nie ma jeszcze żadnego pakietu.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {packages.map(pkg => {
              const meta = STATUS_META[pkg.status] || STATUS_META.active;
              const hasLimit = pkg.sessions_total !== null && pkg.sessions_total !== undefined;
              const pct = hasLimit ? Math.min(100, Math.round((pkg.sessions_used / pkg.sessions_total) * 100)) : 0;
              return (
                <div key={pkg.id} className="card-surface rounded-3xl p-6" data-testid={`package-row-${pkg.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="heading-text text-xl text-white">{pkg.package_label}</h3>
                        <span className={`text-xs font-medium px-3 py-1 rounded-full border ${meta.className}`}>
                          {meta.label}
                        </span>
                      </div>
                      <p className="text-sm text-[#A1A1A1]">
                        {new Date(pkg.purchased_date).toLocaleDateString('pl-PL')} • {pkg.price.toFixed(2)} zł
                        {pkg.payment_due_date && ` • płatność do ${new Date(pkg.payment_due_date).toLocaleDateString('pl-PL')}`}
                      </p>
                      {pkg.notes && <p className="text-sm text-[#A1A1A1] mt-1 italic">{pkg.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {pkg.status === 'active' && (
                        <>
                          <Button
                            onClick={() => handleUseSession(pkg)}
                            disabled={busyId === pkg.id}
                            variant="ghost"
                            size="sm"
                            className="text-[#C9A84C] hover:text-white border border-[#C9A84C]/40 rounded-full"
                            data-testid={`use-session-${pkg.id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Zużyj sesję
                          </Button>
                          {pkg.sessions_used > 0 && (
                            <Button
                              onClick={() => handleUndoSession(pkg)}
                              disabled={busyId === pkg.id}
                              variant="ghost"
                              size="sm"
                              className="text-[#A1A1A1] hover:text-white"
                              title="Cofnij ostatnie zużycie"
                              data-testid={`undo-session-${pkg.id}`}
                            >
                              <Undo2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            onClick={() => handleCancel(pkg)}
                            disabled={busyId === pkg.id}
                            variant="ghost"
                            size="sm"
                            className="text-[#A1A1A1] hover:text-red-400"
                            title="Anuluj pakiet"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {pkg.status === 'completed' && pkg.sessions_used > 0 && (
                        <Button
                          onClick={() => handleUndoSession(pkg)}
                          disabled={busyId === pkg.id}
                          variant="ghost"
                          size="sm"
                          className="text-[#A1A1A1] hover:text-white"
                          title="Cofnij ostatnie zużycie"
                        >
                          <Undo2 className="w-4 h-4 mr-1" /> Cofnij
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDelete(pkg)}
                        disabled={busyId === pkg.id}
                        variant="ghost"
                        size="sm"
                        className="text-[#A1A1A1] hover:text-red-400"
                        title="Usuń pakiet"
                        data-testid={`delete-package-${pkg.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {hasLimit ? (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-[#A1A1A1] mb-2">
                        <span>WYKORZYSTANE SESJE</span>
                        <span>{pkg.sessions_used} / {pkg.sessions_total}</span>
                      </div>
                      <div className="h-2 rounded-full bg-[#0A0A0A] overflow-hidden">
                        <div className="h-full bg-[#C9A84C] transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 text-xs text-[#A1A1A1]">
                      Bez limitu sesji • {pkg.sessions_used} zarejestrowanych
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};
